type DayHours = { open: string; close: string } | null;
type OpeningHours = Record<string, DayHours>;

const DAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

/**
 * Check if a place is currently open based on opening hours
 * @param openingHours - The opening hours object from the database
 * @param now - Optional Date object for testing purposes
 * @returns { isOpen, closingSoon, opensAt, closesAt }
 */
export function getOpenStatus(
  openingHours: OpeningHours | null | undefined,
  now: Date = new Date()
): {
  isOpen: boolean;
  closingSoon: boolean;
  opensAt: string | null;
  closesAt: string | null;
  nextOpenDay: string | null;
} {
  if (!openingHours || Object.keys(openingHours).length === 0) {
    return {
      isOpen: false,
      closingSoon: false,
      opensAt: null,
      closesAt: null,
      nextOpenDay: null,
    };
  }

  const dayIndex = now.getDay(); // 0 = Sunday
  const currentDayKey = DAY_KEYS[dayIndex];
  const currentHours = now.getHours();
  const currentMinutes = now.getMinutes();
  const currentTimeMinutes = currentHours * 60 + currentMinutes;

  const todayHours = openingHours[currentDayKey];

  if (todayHours) {
    const [openHour, openMin] = todayHours.open.split(':').map(Number);
    const [closeHour, closeMin] = todayHours.close.split(':').map(Number);
    const openTimeMinutes = openHour * 60 + openMin;
    const closeTimeMinutes = closeHour * 60 + closeMin;

    const isOpen = currentTimeMinutes >= openTimeMinutes && currentTimeMinutes < closeTimeMinutes;
    const closingSoon = isOpen && (closeTimeMinutes - currentTimeMinutes) <= 30;

    if (isOpen) {
      return {
        isOpen: true,
        closingSoon,
        opensAt: todayHours.open,
        closesAt: todayHours.close,
        nextOpenDay: null,
      };
    }

    // Not yet open today
    if (currentTimeMinutes < openTimeMinutes) {
      return {
        isOpen: false,
        closingSoon: false,
        opensAt: todayHours.open,
        closesAt: todayHours.close,
        nextOpenDay: null,
      };
    }
  }

  // Find next open day
  for (let i = 1; i <= 7; i++) {
    const nextDayIndex = (dayIndex + i) % 7;
    const nextDayKey = DAY_KEYS[nextDayIndex];
    const nextDayHours = openingHours[nextDayKey];
    
    if (nextDayHours) {
      const dayNames: Record<string, string> = {
        sunday: 'Dim',
        monday: 'Lun',
        tuesday: 'Mar',
        wednesday: 'Mer',
        thursday: 'Jeu',
        friday: 'Ven',
        saturday: 'Sam',
      };
      
      return {
        isOpen: false,
        closingSoon: false,
        opensAt: nextDayHours.open,
        closesAt: null,
        nextOpenDay: dayNames[nextDayKey],
      };
    }
  }

  return {
    isOpen: false,
    closingSoon: false,
    opensAt: null,
    closesAt: null,
    nextOpenDay: null,
  };
}

/**
 * Format opening hours for display
 */
export function formatOpenStatus(status: ReturnType<typeof getOpenStatus>): {
  label: string;
  variant: 'open' | 'closing' | 'closed';
  detail: string | null;
} {
  if (status.isOpen) {
    if (status.closingSoon) {
      return {
        label: 'Ferme bientôt',
        variant: 'closing',
        detail: `Ferme à ${status.closesAt}`,
      };
    }
    return {
      label: 'Ouvert',
      variant: 'open',
      detail: `Jusqu'à ${status.closesAt}`,
    };
  }

  if (status.opensAt && !status.nextOpenDay) {
    return {
      label: 'Fermé',
      variant: 'closed',
      detail: `Ouvre à ${status.opensAt}`,
    };
  }

  if (status.nextOpenDay && status.opensAt) {
    return {
      label: 'Fermé',
      variant: 'closed',
      detail: `Ouvre ${status.nextOpenDay} à ${status.opensAt}`,
    };
  }

  return {
    label: 'Fermé',
    variant: 'closed',
    detail: null,
  };
}
